import { useEffect, useRef, useState } from 'react';
import { createSubmissionSocket } from '../ws';
import { submissionsApi } from '../api/submissions';
import type { SubmissionResult } from '@/features/ProblemWorkspace/TestCasePanel';

const PENDING_VERDICTS = new Set(['PENDING', 'JUDGING']);

export function useSubmissionResult(submissionId: string | null) {
  const [result, setResult] = useState<SubmissionResult | null>(null);
  const [isJudging, setIsJudging] = useState(false);
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (!submissionId) return;
    setIsJudging(true);
    setResult(null);
    resolvedRef.current = false;

    function resolve(data: SubmissionResult) {
      if (resolvedRef.current) return;
      resolvedRef.current = true;
      setResult(data);
      setIsJudging(false);
    }

    const cleanup = createSubmissionSocket(
      submissionId,
      (data) => resolve(data as SubmissionResult),
      () => setIsJudging(false)
    );

    // Polling fallback: handles race condition where judge finishes before WS connects
    const pollInterval = setInterval(async () => {
      if (resolvedRef.current) {
        clearInterval(pollInterval);
        return;
      }
      try {
        const sub = await submissionsApi.getById(submissionId);
        if (!PENDING_VERDICTS.has(sub.verdict)) {
          clearInterval(pollInterval);
          resolve({
            submission_id: sub.id,
            verdict: sub.verdict,
            runtime_ms: sub.runtime_ms ?? 0,
            memory_kb: sub.memory_kb ?? 0,
            passed_cases: sub.passed_cases,
            total_cases: sub.total_cases,
            error_msg: sub.error_msg,
          });
        }
      } catch { /* ignore */ }
    }, 1500);

    const timeout = setTimeout(() => {
      setIsJudging(false);
      clearInterval(pollInterval);
    }, 30_000);

    return () => {
      cleanup();
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [submissionId]);

  return { result, isJudging };
}
